<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  rootTag: {
    type: String,
    default: 'form',
    validator: value => ['form', 'div'].includes(value),
  },
  saving: { type: Boolean, default: false },
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const { isWorkspaceV2 } = useWorkspaceV2();

const initialState = {
  name: '',
  description: '',
  productName: '',
  featureFaq: false,
  featureMemory: false,
  featureCitation: false,
};

const state = reactive({ ...initialState });
const baseline = ref(JSON.stringify(state));
const isDirty = computed(() => JSON.stringify(state) !== baseline.value);

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => props.saving);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.ASSISTANTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  description: getErrorMessage('description', 'DESCRIPTION'),
  productName: getErrorMessage('productName', 'PRODUCT_NAME'),
}));

const handleCancel = () => emit('cancel');

const prepareAssistantDetails = () => ({
  name: state.name,
  description: state.description,
  config: {
    ...(props.mode === 'edit' ? props.assistant?.config : {}),
    product_name: state.productName,
    feature_faq: state.featureFaq,
    feature_memory: state.featureMemory,
    feature_citation: state.featureCitation,
  },
});

const handleSubmit = async () => {
  if (isLoading.value) return;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || isLoading.value) {
    return;
  }

  emit('submit', prepareAssistantDetails());
};

const handleFieldEnter = event => {
  if (props.rootTag === 'form' || event.isComposing || event.defaultPrevented)
    return;
  event.preventDefault();
  handleSubmit();
};

const updateStateFromAssistant = assistant => {
  if (!assistant) return;

  const { name = '', description = '', config = {} } = assistant;

  Object.assign(state, {
    name,
    description,
    productName: config.product_name || '',
    featureFaq: config.feature_faq || false,
    featureMemory: config.feature_memory || false,
    featureCitation: config.feature_citation || false,
  });
};

watch(
  () => props.assistant,
  newAssistant => {
    if (props.mode === 'edit' && newAssistant) {
      updateStateFromAssistant(newAssistant);
    } else {
      Object.assign(state, initialState);
    }
    baseline.value = JSON.stringify(state);
    v$.value.$reset();
  },
  { immediate: true }
);
defineExpose({ isDirty, submit: handleSubmit });
</script>

<template>
  <component
    :is="rootTag"
    class="flex flex-col gap-4"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.name"
      :disabled="isLoading"
      :custom-input-class="isWorkspaceV2 ? '!h-11' : ''"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
      @keydown.enter="handleFieldEnter"
    />

    <Editor
      v-model="state.description"
      :disabled="isLoading"
      :editor-label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
    />

    <Input
      v-model="state.productName"
      :disabled="isLoading"
      :custom-input-class="isWorkspaceV2 ? '!h-11' : ''"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
      @keydown.enter="handleFieldEnter"
    />

    <fieldset
      :disabled="isLoading"
      class="flex flex-col gap-2.5"
      :class="{ 'm-0 border-0 p-0': isWorkspaceV2 }"
    >
      <legend class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </legend>

      <label
        class="flex items-center gap-2"
        :class="{ 'min-h-11': isWorkspaceV2 }"
      >
        <input v-model="state.featureFaq" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </span>
      </label>

      <label
        class="flex items-center gap-2"
        :class="{ 'min-h-11': isWorkspaceV2 }"
      >
        <input v-model="state.featureMemory" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </span>
      </label>

      <label
        class="flex items-center gap-2"
        :class="{ 'min-h-11': isWorkspaceV2 }"
      >
        <input v-model="state.featureCitation" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </span>
      </label>
    </fieldset>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        :class="{ '!h-11': isWorkspaceV2 }"
        :disabled="isLoading"
        @click="handleCancel"
      />
      <Button
        :type="rootTag === 'form' ? 'submit' : 'button'"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :class="{ '!h-11': isWorkspaceV2 }"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="rootTag !== 'form' && handleSubmit()"
      />
    </div>
  </component>
</template>
